import React, { useEffect, useState } from "react";
import "./AddReportAttributesForm.css";
import TextFieldInput from "../../../Atoms/Inputs/TextField/TextFieldInput";
import PrimaryButton from "../../../Atoms/Buttons/Primary/PrimaryButton";
import {
  Box,
  IconButton,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import useSave from "../../../../Hooks/useSave";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import { Formik } from "formik";
import * as Yup from "yup";
import { ageGroupsURL, missionsURL } from "../../../../Hooks/URL";
import useFetch from "../../../../Hooks/useFetch";
import axios from "axios";

// const CourseSchema = Yup.object().shape({
//   title: Yup.string().required("Program name required !!"),
//   description: Yup.string().required("Description required !!"),
// });

const GroupAttributes = ({ group, index, attributes, setAttributes }) => {
  const [name, setAttributesName] = useState("");
  const [maxValue, setMaxValue] = useState("");
  const [lastID, setLastId] = useState(1);
  const handleAddAttribute = () => {
    if (!name || !maxValue || maxValue <= 0) {
      return;
    }
    setAttributes(
      [
        ...attributes?.filter((item) => item?.number != group?.number),
        {
          ...attributes?.find((item) => item?.number == group?.number),
          attributes: [
            ...attributes?.find((item) => item?.number == group?.number)
              ?.attributes,
            {
              number: lastID,
              name: name,
              maxValue: maxValue,
            },
          ],
        },
      ].sort((a, b) => a.number - b.number)
    );
    setAttributesName("");
    setLastId(lastID + 1);
    setMaxValue("");
  };
  const handleDeleteAttribute = (name) => {
    setAttributes(
      [
        ...attributes?.filter((item) => item?.number != group?.number),
        {
          ...attributes?.find((item) => item?.number == group?.number),
          attributes: [
            ...attributes
              ?.find((item) => item?.number == group?.number)
              ?.attributes?.filter((att) => att?.name != name),
          ],
        },
      ].sort((a, b) => a.number - b.number)
    );
  };
  return (
    <Box
      key={index}
      className="missionInfoSection"
      sx={{ marginBottom: "16px", position: "relative", width:"100%" }}
    >
      <Box className="missionSettingsHeader">
        <Typography
          variant="h4"
          className="missionSettingsTitle"
          sx={{
            fontSize: { xs: "17px", md: "20px" },
            fontWeight: "600",
          }}
        >
          Group {index + 1}: {group?.name}
        </Typography>
        <IconButton
          size="small"
          sx={{
            cursor: "pointer",
            color: "#757575",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.04)",
            },
          }}
          onClick={() => {
            setAttributes(
              attributes?.filter((item) => item?.name != group?.name)
            );
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          gap: "10px",
          alignItems: "center",
          marginTop: "12px",
        }}
      >
        <TextFieldInput
          Required={false}
          fullWidth
          type="text"
          placeholder={"Enter Attribute Name"}
          className="Input"
          name={"name"}
          newValue={name}
          change={(e) => {
            setAttributesName(e.target.value);
          }}
        />
        <TextFieldInput
          Required={false}
          fullWidth
          type="number"
          placeholder={"Enter Attribute Max Value"}
          className="Input"
          name={"maxValue"}
          newValue={maxValue}
          change={(e) => {
            setMaxValue(e.target.value);
          }}
        />
        <IconButton
          sx={{ height: "fit-content" }}
          onClick={() => {
            handleAddAttribute();
          }}
        >
          <AddIcon />
        </IconButton>
      </Box>
      <Box sx={{ display: "flex", gap: "10px", flexWrap: "wrap", marginTop: "12px" }}>
        {attributes
          ?.find((item) => item?.name == group?.name)
          ?.attributes?.map((att, a) => (
            <Box
              key={a}
              className="missionInfoItem"
              sx={{
                padding: "25px 15px 15px",
                position: "relative",
                minWidth: "150px",
              }}
            >
              <IconButton
                size="small"
                sx={{
                  cursor: "pointer",
                  position: "absolute",
                  right: "5px",
                  top: "5px",
                  color: "#757575",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                  },
                }}
                onClick={() => {
                  handleDeleteAttribute(att?.name);
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
              <Typography
                variant="body2"
                sx={{
                  color: "#6b7280",
                  fontSize: "12px",
                  marginBottom: "4px",
                  fontWeight: 500,
                }}
              >
                {att?.name}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "var(--text-primary)",
                  fontWeight: 600,
                  fontSize: "15px",
                }}
              >
                Max: {att?.maxValue}
              </Typography>
            </Box>
          ))}
      </Box>
    </Box>
  );
};

function AddReportAttributesForm({ missionID, setOpen }) {
  const [missionToImport, setMissionToImport] = useState(null);
  const [selectedMissionToImport, setSelectedMissionToImport] = useState("");
  const [newObject, setNewObject] = useState({});
  const [lastID, setLastID] = useState(1);
  const [attributes, setAttributes] = useState([]);
  const [goSave, setGoSave] = useState(false);

  // Dynamic URL based on whether a mission is selected for import
  const getApiUrl = () => {
    if (selectedMissionToImport) {
      return `${missionsURL}/${missionID}/report/import/missions/${selectedMissionToImport}`;
    }
    return `${missionsURL}/${missionID}/report`;
  };

  const { saveState, loading: addLoading } = useSave({
    url: getApiUrl(),
    goSave,
    setGoSave,
    newObject,
    refreshAfter: true,
  });

  const {
    data: missionsToImportData,
    loading: missionsToImportLoading,
    error: missionsToImportError,
  } = useFetch(`${missionsURL}/${missionID}/report/import/missions`);

  useEffect(() => {
    if (
      !missionsToImportLoading &&
      !missionsToImportError &&
      missionsToImportData
    ) {
      setMissionToImport(missionsToImportData?.data);
    }
  }, [missionsToImportData, missionsToImportLoading, missionsToImportError]);

  console.log(attributes);

  // Handle mission selection and populate form data
  const handleMissionImport = (missionId) => {
    if (missionId && missionToImport) {
      const selectedMission = missionToImport.find(
        (mission) => mission.id === missionId
      );
      if (selectedMission && selectedMission.report) {
        setSelectedMissionToImport(missionId);
      }
    } else {
      // Clear the form when no mission is selected
      setAttributes([]);
      setLastID(1);
      setSelectedMissionToImport("");
    }
  };

  useEffect(() => {
    if (saveState.dataRes.status == "success") {
      setOpen(false);
    }
  }, [saveState]);

  const handleAddReportAttributeGroup = (value, setValue) => {
    if (value && value.trim() !== "") {
      setAttributes([
        ...attributes,
        { number: lastID, name: value.trim(), attributes: [] },
      ]);
      setLastID(lastID + 1);
      setValue("");
    }
  };

  return (
    <div className="CourseSmallFormWrapper">
      {/* Mission Import Choice List */}
      {missionToImport && missionToImport.length > 0 && (
        <Box sx={{ marginBottom: "20px" }}>
          <Typography
            variant="h6"
            sx={{ marginBottom: "10px", color: "var(--text-primary)" }}
          >
            Import from Existing Mission
          </Typography>
          <FormControl fullWidth>
            <InputLabel id="mission-import-label">
              Choose Mission to Import
            </InputLabel>
            <Select
              labelId="mission-import-label"
              id="mission-import-select"
              value={selectedMissionToImport}
              label="Choose Mission to Import"
              onChange={(e) => handleMissionImport(e.target.value)}
            >
              <MenuItem value="">
                <em>Select a mission to import from</em>
              </MenuItem>
              {missionToImport.map((mission) => (
                <MenuItem key={mission.id} value={mission.id}>
                  {mission.order} - {mission.ageGroup?.name} -{" "}
                  {mission.ageGroup?.category?.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {/* OR separator */}
      {missionToImport && missionToImport.length > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            marginBottom: "20px",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              height: "1px",
              backgroundColor: "#ccc",
              flex: 1,
            }}
          ></div>
          <Typography
            variant="h6"
            sx={{
              margin: "0 15px",
              color: "black",
              fontWeight: "500",
            }}
          >
            OR
          </Typography>
          <div
            style={{
              height: "1px",
              backgroundColor: "#ccc",
              flex: 1,
            }}
          ></div>
        </Box>
      )}

      <Formik
        initialValues={{}}
        onSubmit={(values) => {
          if (selectedMissionToImport) {
            // If a mission is selected, send empty object (the URL contains the mission ID)
            setNewObject({});
          } else {
            // If no mission is selected, send the form data
            setNewObject({ groups: attributes });
          }
          setGoSave(true);
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
        }) => (
          <form autoComplete="off" onSubmit={handleSubmit}>
            <div className="ReportFormContainer">
              {/* Only show the input and groups when no mission is selected */}
              {!selectedMissionToImport && (
                <>
                  <TextFieldInput
                    Required={false}
                    fullWidth
                    type="text"
                    setAttributes={setAttributes}
                    attribute={attributes || []}
                    EndAdornment={<AddIcon />}
                    placeholder={"Enter Group Title"}
                    className="Input"
                    label={"Group Title"}
                    name={"group"}
                    handleAddReportAttribute={handleAddReportAttributeGroup}
                  />
                  <div
                    style={{
                      display: "flex",
                      gap: "10px",
                      alignItems: "center",
                      width: "100%",
                      flexWrap: "wrap",
                    }}
                  >
                    {attributes?.map((group, index) => (
                      <GroupAttributes
                        group={group}
                        index={index}
                        attributes={attributes}
                        setAttributes={setAttributes}
                      />
                    ))}
                  </div>
                </>
              )}

              {/* Show selected mission info when a mission is selected */}
              {selectedMissionToImport && (
                <Box className="missionInfoSection">
                  <Box className="missionSettingsHeader">
                    <Typography
                      variant="h4"
                      className="missionSettingsTitle"
                      sx={{
                        fontSize: { xs: "17px", md: "20px" },
                        fontWeight: "600",
                      }}
                    >
                      Importing from Mission:{" "}
                      {
                        missionToImport.find(
                          (m) => m.id === selectedMissionToImport
                        )?.order
                      }{" "}
                      -{" "}
                      {
                        missionToImport.find(
                          (m) => m.id === selectedMissionToImport
                        )?.ageGroup?.name
                      }
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#6b7280",
                      fontSize: "14px",
                      marginTop: "8px",
                    }}
                  >
                    The report attributes from this mission will be imported.
                    Click Submit to confirm.
                  </Typography>
                </Box>
              )}
            </div>

            <div style={{ display: "flex", justifyContent: "center" }}>
              <PrimaryButton
                classBtn={"center addButton"}
                btnType={"submit"}
                text={selectedMissionToImport ? "Import Report" : "Add"}
                loading={addLoading}
                style={{
                  textTransform: "capitalize",
                  marginTop: "15px",
                  width: "200px",
                  borderRadius: "5px",
                  color: "var(--text-secondary)",
                  backgroundColor: "var(--primary)",
                }}
              />
            </div>
          </form>
        )}
      </Formik>
    </div>
  );
}

export default AddReportAttributesForm;
